import type { AxiosPromise } from "axios";
import axiosInstance from "@/services/axiosInstance";
import type ICompanyInformation from "@/types/CompanyInformation";
import type {
    AddressTypeEnum,
    BrandDashboardCount,
    PaginatedCompanyAddressList,
    CompanyDocument,
    PaginatedCompanyList,
    CompanyAddress,
    PaginatedMerchantOfRecordCompanyList,
    MerchantOfRecordSupplier,
    PatchedMerchantOfRecordSupplierUpdate,
    OnboardingSupplier,
    OnboardingSupplierCreate,
    PaginatedOnboardingSupplierListList,
    PatchedOnboardingSupplierUpdate,
    FinalSupplierCreate,
    PaginatedStaffUserList,
    SupplierSite,
    MerchantOfRecordSupplierSiteCreate,
    DocumentType,
    SupplierDocument,
    PaginatedSupplierDocumentList,
    MerchantOfRecordBuyerDetail,
    SupplierTypeEnum,
    AvailableSitesResponse,
    PaginatedSupplierBuyerList,
    RegistrationValidationInput,
    RegistrationValidationResponse,
} from "@/types/api/data-contracts";

/**
 * Address structure for buyer creation/update payloads.
 * Note: These types will be auto-generated once the backend is deployed.
 * Run `npm run generate-schema:staging` to update.
 */
export interface MerchantOfRecordBuyerAddress {
    address_line_1: string;
    address_line_2?: string | null;
    town: string;
    region?: string | null;
    postcode: string;
    country: string;
    vat_country_code?: string | null;
    vat_number?: string | null;
    ein?: string | null;
}

/**
 * Payload for creating a new buyer as a Merchant of Record user.
 */
export interface MerchantOfRecordBuyerCreate {
    first_name: string;
    last_name: string;
    email: string;
    company_name: string;
    registration_name?: string | null;
    product_type: string[];
    registration_number?: string | null;
    website?: string | null;
    buyer_accounts_email_address?: string | null;
    buyer_delivery_email_address?: string | null;
    phone_country_code?: string | null;
    phone_number?: string | null;
    duns?: string | null;
    company_address: MerchantOfRecordBuyerAddress;
    send_welcome_email: boolean;
}

export interface TwoBuyerConfigUpdate {
    representative_first_name?: string;
    representative_last_name?: string;
    representative_email?: string;
    representative_phone_number?: string;
    invoice_emails?: string[];
}

/**
 * Payload for updating an existing buyer as a Merchant of Record user.
 * All fields are optional for partial updates.
 */
export interface PatchedMerchantOfRecordBuyerUpdate {
    first_name?: string;
    last_name?: string;
    company_name?: string;
    registration_name?: string | null;
    product_type?: string[];
    registration_number?: string | null;
    website?: string | null;
    buyer_accounts_email_address?: string | null;
    buyer_delivery_email_address?: string | null;
    phone_country_code?: string | null;
    phone_number?: string | null;
    duns?: string | null;
    /** Sending "" clears the value. Max length 100. */
    hq_city?: string | null;
    company_address?: MerchantOfRecordBuyerAddress;
    two_buyer_config?: TwoBuyerConfigUpdate;
}

/**
 * Single Companies House search result. Will be auto-generated once the
 * backend is deployed — run `npm run generate-schema:staging`.
 */
export interface CompaniesHouseSearchResult {
    registration_name: string;
    registration_number: string;
}

/**
 * Response from the Companies House autocomplete search endpoint.
 */
export interface CompaniesHouseSearchResponse {
    results: CompaniesHouseSearchResult[];
}

class CompanyService {
    // eslint-disable-next-line no-use-before-define
    private static instance: CompanyService;

    public static getInstance(): CompanyService {
        if (!CompanyService.instance) {
            CompanyService.instance = new CompanyService();
        }
        return CompanyService.instance;
    }

    getInfo() {
        return axiosInstance.get("companies/info/");
    }

    addAddress(payload: object) {
        return axiosInstance.post("companies/addresses/", payload);
    }

    updateAddress(addressId: number, payload: object) {
        return axiosInstance.put(`companies/addresses/${addressId}/`, payload);
    }

    getAddresses(
        page?: number,
        addressType?: `${AddressTypeEnum}`
    ): AxiosPromise<PaginatedCompanyAddressList> {
        return axiosInstance.get("companies/addresses/", {
            params: { page, address_type: addressType },
        });
    }

    getDeliveryAddress(id: string): AxiosPromise {
        const url = `/inquiries/delivery_address/${id}`;
        return axiosInstance.get(url);
    }

    updateDeliveryAddress(id: string, payload: any): AxiosPromise {
        const url = `/inquiries/delivery_address/${id}`;
        return axiosInstance.put(url, payload);
    }

    getDispatchAddress(id: string | number): AxiosPromise {
        const url = `/inquiries/dispatch_address/${id}`;
        return axiosInstance.get(url);
    }

    updateDispatchAddress(id: string, payload: any): AxiosPromise {
        const url = `/inquiries/dispatch_address/${id}`;
        return axiosInstance.put(url, payload);
    }

    getAddressDetails(addressId: number) {
        return axiosInstance.get(`companies/addresses/${addressId}/`);
    }

    deleteAddress(addressId: number) {
        return axiosInstance.delete(`companies/addresses/${addressId}/`);
    }

    getDashboardStats(): AxiosPromise<BrandDashboardCount> {
        return axiosInstance.get("companies/brands/dashboard/count/");
    }

    inviteBrand(payload: object) {
        return axiosInstance.post(`companies/invite-company/`, {
            ...payload,
            referred_company_type: "brand",
            signup_link: import.meta.env.VITE_BASE_URL as string,
        });
    }

    inviteSupplier(payload: object) {
        return axiosInstance.post(`companies/invite-company/`, {
            ...payload,
            referred_company_type: "supplier",
            signup_link: import.meta.env.VITE_BASE_URL as string,
        });
    }

    addCustomer(payload: object) {
        return axiosInstance.post("companies/customers/", payload);
    }

    uploadDocument(payload: FormData): AxiosPromise<CompanyDocument> {
        const url = "companies/documents/";
        return axiosInstance.post(url, payload);
    }

    getBrandsForMorUser(
        search: string = ""
    ): AxiosPromise<PaginatedCompanyList> {
        const url = "/companies/merchant-of-record-brands/";
        return axiosInstance.get(url, { params: { search } });
    }

    getSuppliersForMorUser(
        search: string = ""
    ): AxiosPromise<PaginatedCompanyList> {
        const url = "/companies/merchant-of-record-suppliers/";
        return axiosInstance.get(url, { params: { search } });
    }

    getCompanyAddressByCompany(
        companyUuid: string
    ): AxiosPromise<CompanyAddress[]> {
        return axiosInstance.get(
            `/companies/merchant-of-record-company-addresses/?company_uuid=${companyUuid}`
        );
    }

    createCompanyAddress(
        payload: Partial<CompanyAddress>,
        companyUuid: string
    ): AxiosPromise<CompanyAddress> {
        return axiosInstance.post(
            "/companies/merchant-of-record-company-addresses/",
            { ...payload, company_uuid: companyUuid }
        );
    }

    getBuyersForMerchantOfRecordUser({
        page,
        search,
    }: {
        page?: number;
        search?: string;
    }): AxiosPromise<PaginatedMerchantOfRecordCompanyList> {
        return axiosInstance.get("/companies/merchant-of-record-brands/", {
            params: { page, search },
        });
    }

    getSuppliersForMerchantOfRecordUser({
        page,
        search,
    }: {
        page?: number;
        search?: string;
    }): AxiosPromise<PaginatedMerchantOfRecordCompanyList> {
        return axiosInstance.get("/companies/merchant-of-record-suppliers/", {
            params: { page, search },
        });
    }

    getSupplierDetailsForMerchantOfRecordUser(
        uuid: string
    ): AxiosPromise<MerchantOfRecordSupplier> {
        return axiosInstance.get(
            `/companies/merchant-of-record-suppliers/${uuid}/`
        );
    }

    getSupplierDocumentTypes(): AxiosPromise<DocumentType[]> {
        return axiosInstance.get(`/companies/supplier-document-types`);
    }

    getAvailableSupplierDocumentTypes(
        supplierUuid: string
    ): AxiosPromise<DocumentType[]> {
        return axiosInstance.get(
            `/companies/suppliers/${supplierUuid}/supplier-documents/available-types`
        );
    }

    getSupplierDocuments(
        supplierUuid: string,
        params?: {
            page?: number;
            page_size?: number;
            ordering?: string;
            type_uuid__in?: string;
            site_uuid__in?: string;
            search?: string;
            archived?: boolean;
        }
    ): AxiosPromise<PaginatedSupplierDocumentList> {
        return axiosInstance.get(
            `/companies/suppliers/${supplierUuid}/supplier-documents/`,
            {
                params,
            }
        );
    }

    getSupplierSites(uuid: string): AxiosPromise<SupplierSite[]> {
        return axiosInstance.get(
            `/companies/suppliers/${uuid}/supplier-documents/sites/`
        );
    }

    /**
     * Expire a supplier document now (sets end_date to today). MoR only.
     * @see SC-4217
     */
    expireSupplierDocument(
        supplierUuid: string,
        documentUuid: string
    ): AxiosPromise<SupplierDocument> {
        return axiosInstance.post(
            `/companies/suppliers/${supplierUuid}/supplier-documents/${documentUuid}/expire/`
        );
    }

    getSupplierBuyers(
        supplierUuid: string,
        params?: { page?: number; page_size?: number; search?: string }
    ): AxiosPromise<PaginatedSupplierBuyerList> {
        return axiosInstance.get(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/buyers/`,
            { params }
        );
    }

    /**
     * Get merged list of existing sites and dispatch address names for document upload.
     * Backend will auto-create a SupplierSite if site_name is provided during document creation.
     * @see SC-2293
     */
    getAvailableSites(
        supplierUuid: string
    ): AxiosPromise<AvailableSitesResponse> {
        return axiosInstance.get(
            `/companies/suppliers/${supplierUuid}/supplier-documents/available-sites/`
        );
    }

    createSupplierSiteForMerchantOfRecordUser(
        uuid: string,
        payload: MerchantOfRecordSupplierSiteCreate
    ): AxiosPromise<SupplierSite> {
        return axiosInstance.post(
            `/companies/merchant-of-record-suppliers/${uuid}/sites/`,
            payload
        );
    }

    updateSupplierDetailsForMerchantOfRecordUser(
        uuid: string,
        payload: PatchedMerchantOfRecordSupplierUpdate
    ): AxiosPromise<MerchantOfRecordSupplier> {
        return axiosInstance.patch(
            `/companies/merchant-of-record-suppliers/${uuid}/update/`,
            payload
        );
    }

    updateSupplierCompanyInfoForMerchantOfRecordUser(
        uuid: string,
        payload: {
            duns?: string | null;
            supplier_type?: SupplierTypeEnum | null;
            supplier_lead_time_days?: number | null;
        }
    ): AxiosPromise<MerchantOfRecordSupplier> {
        return axiosInstance.patch(
            `/companies/merchant-of-record-suppliers/${uuid}/update/`,
            payload
        );
    }

    // Onboarding Suppliers endpoints for Buyer users
    getOnboardingSuppliersForBuyerUser({
        page,
        search,
        status,
    }: {
        page?: number;
        search?: string;
        status?: string;
    }): AxiosPromise<PaginatedOnboardingSupplierListList> {
        return axiosInstance.get("/companies/buyer/onboarding-suppliers/", {
            params: { page, search, status },
        });
    }

    getOnboardingSupplierDetailsForBuyerUser(
        uuid: string
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.get(
            `/companies/buyer/onboarding-suppliers/${uuid}/`
        );
    }

    // Onboarding Suppliers endpoints for MoR users
    getOnboardingSuppliersForMerchantOfRecordUser({
        page,
        search,
        status,
        buyerUuid,
        isArchived,
    }: {
        page?: number;
        search?: string;
        status?: string;
        buyerUuid?: string;
        isArchived?: boolean;
    }): AxiosPromise<PaginatedOnboardingSupplierListList> {
        return axiosInstance.get("/companies/mor/onboarding-suppliers/", {
            params: {
                page,
                search,
                status,
                buyer_uuid: buyerUuid,
                is_archived: isArchived,
            },
        });
    }

    getOnboardingSupplierDetailsForMerchantOfRecordUser(
        uuid: string
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.get(
            `/companies/mor/onboarding-suppliers/${uuid}/`
        );
    }

    getStaffUsersForMerchantOfRecord(
        search?: string
    ): AxiosPromise<PaginatedStaffUserList> {
        const params = search ? { search } : {};
        return axiosInstance.get(
            `/companies/mor/onboarding-suppliers/staff-users/`,
            { params }
        );
    }

    createOnboardingSupplierForMerchantOfRecordUser(
        payload: OnboardingSupplierCreate
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.post(
            "/companies/mor/onboarding-suppliers/",
            payload
        );
    }

    updateOnboardingSupplierStatusForMerchantOfRecordUser(
        uuid: string,
        payload: PatchedOnboardingSupplierUpdate
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.patch(
            `/companies/mor/onboarding-suppliers/${uuid}/`,
            payload
        );
    }

    createFinalSupplierForOnboardingSupplier(
        uuid: string,
        payload: FinalSupplierCreate
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.post(
            `/companies/mor/onboarding-suppliers/${uuid}/create-final-supplier/`,
            payload
        );
    }

    deleteOnboardingSupplier(uuid: string): AxiosPromise<void> {
        return axiosInstance.delete(
            `/companies/mor/onboarding-suppliers/${uuid}/`
        );
    }

    archiveOnboardingSupplier(uuid: string): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.post(
            `/companies/mor/onboarding-suppliers/${uuid}/archive/`
        );
    }

    unarchiveOnboardingSupplier(
        uuid: string
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.post(
            `/companies/mor/onboarding-suppliers/${uuid}/unarchive/`
        );
    }

    createBuyerForMerchantOfRecordUser(
        payload: MerchantOfRecordBuyerCreate
    ): AxiosPromise<MerchantOfRecordBuyerDetail> {
        return axiosInstance.post(
            "/companies/merchant-of-record-buyers/",
            payload
        );
    }

    updateBuyerForMerchantOfRecordUser(
        buyerUuid: string,
        payload: PatchedMerchantOfRecordBuyerUpdate
    ): AxiosPromise<MerchantOfRecordBuyerDetail> {
        return axiosInstance.patch(
            `/companies/merchant-of-record-buyers/${buyerUuid}/`,
            payload
        );
    }

    validateRegistration(
        payload: RegistrationValidationInput,
        signal?: AbortSignal
    ): AxiosPromise<RegistrationValidationResponse> {
        return axiosInstance.post(
            "/companies/validate-registration/",
            payload,
            { signal }
        );
    }

    searchCompaniesHouse(
        q: string,
        signal?: AbortSignal,
        limit: number = 10
    ): AxiosPromise<CompaniesHouseSearchResponse> {
        return axiosInstance.get("/companies/companies-house/search/", {
            params: { q, limit },
            signal,
        });
    }

    acknowledgeMarketplaceTc(): AxiosPromise<ICompanyInformation> {
        return axiosInstance.post(
            "/companies/brands/acknowledge-marketplace-terms-and-conditions/"
        );
    }
}

export default CompanyService;
